"use client";
import { useState } from "react";
import {
  CheckCircle,
  ArrowRight,
  Bolt,
  Star,
  Users,
  Zap,
  FileText,
} from "lucide-react";
import {
  accessControlConsoleApi,
  accessWebAppApi,
} from "../../services/authService";
import "../portalSelector/portalSelector.css";
import { handleApiError } from "../shared/utils";
import Logo from "../../assests/images/E3CoE_NOAHITxt.png";

export default function PortalSelector() {
  const [isLoading, setIsLoading] = useState(false);

  const getAccessLevel = () => {
    const level = sessionStorage.getItem("accessLevel");
    if (level == 0) {
      return "assessment-only";
    } else {
      return "dual-access";
    }
  };

  const [userType, setUserType] = useState(getAccessLevel());

  const userTypes = [
    {
      id: 1,
      title: "Assessment Taker",
      description:
        "Complete comprehensive soft skills assessments and unlock personalized career insights",
      icon: Star,
      highlight: true,
    },
    {
      id: 2,
      title: "Business User",
      description:
        "Invite participants, monitor progress, and gain strategic workforce insights",
      icon: Users,
      highlight: false,
    },
    {
      id: 3,
      title: "Dual Access",
      description:
        "Access both platforms with full capabilities for complete talent intelligence",
      icon: Bolt,
      highlight: false,
    },
  ];

  const assessmentSteps = [
    {
      id: 1,
      title: "Complete Your Sprints",
      description:
        "Finish 3 adaptive sprints with 27 questions that evaluate your soft skills and problem-solving approach in just 10–15 minutes.",
      icon: Zap,
    },
    {
      id: 2,
      title: "Build Your Career Profile",
      description:
        "Proceed to the Career Profile Assessment and upload your resume to enable deeper insights and contextual analysis.",
      icon: FileText,
    },
    {
      id: 3,
      title: "Get Your Personalized Report",
      description:
        "Receive a comprehensive report with career recommendations, a soft skill analysis, and a custom learning plan designed to accelerate your growth.",
      icon: Star,
    },
  ];

  const handleNavigateToMayaMaya = async () => {
    setIsLoading(true);
    try {
      const obj = {
        email: sessionStorage.getItem("email"),
      };
      const response = await accessWebAppApi(obj);
      if (response.code === 200) {
        setIsLoading(false);
        window.location.href = response.data.url;
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleNavigateToConsole = async () => {
    setIsLoading(true);
    try {
      const obj = {
        email: sessionStorage.getItem("email"),
      };
      const response = await accessControlConsoleApi(obj);
      if (response.code === 200) {
        setIsLoading(false);
        window.location.href = response.data.url;
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const getTimelineItems = () => {
    if (userType === "assessment-only") {
      return assessmentSteps;
    }
    return userTypes;
  };

  return (
    <div className="upskill-wrapper">
      <div className="upskill-bg-container">
        <div className="upskill-gradient-blob upskill-blob-1"></div>
        <div className="upskill-gradient-blob upskill-blob-2"></div>
      </div>

      <div className="upskill-main-content">
        <div className="portal-left-column">
          <div className="upskill-header">
            <h1 className="upskill-title">
              {userType === "assessment-only"
                ? "Welcome to Noah Assessment"
                : "Welcome to Noah"}
            </h1>
            <p className="upskill-subtitle">
              {userType === "assessment-only"
                ? "Complete comprehensive assessments and unlock personalized career insights tailored to your growth."
                : "A unified platform for talent assessment and management. Choose your path or access both."}
            </p>
          </div>

          <div className="upskill-progress-container">
            <div className="upskill-timeline">
              {getTimelineItems().map((timelineItem, index) => {
                const Icon = timelineItem.icon;
                const timelineItems = getTimelineItems();

                return (
                  <div key={timelineItem.id} className="upskill-timeline-item">
                    {/* Timeline line */}
                    {index < timelineItems.length - 1 && (
                      <div className="upskill-timeline-line upskill-timeline-line-active"></div>
                    )}

                    {/* Timeline node */}
                    <div className="upskill-timeline-node-wrapper">
                      <div
                        className={`upskill-timeline-node upskill-node-active ${
                          timelineItem.highlight ? "upskill-node-highlight" : ""
                        }`}
                      >
                        <Icon size={24} className="upskill-node-icon" />
                      </div>
                    </div>

                    {/* Step content */}
                    <div className="upskill-step-content-wrapper">
                      <div className="upskill-step-header">
                        <h3 className="upskill-step-title">
                          {timelineItem.title}
                        </h3>
                      </div>
                      <p className="upskill-step-description">
                        {timelineItem.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="upskill-action-section">
              <div className="upskill-action-card">
                <div className="upskill-action-content">
                  <h3 className="upskill-action-title">
                    {userType === "assessment-only"
                      ? "Start Your Journey"
                      : "Ready to get started?"}
                  </h3>
                  <p className="upskill-action-description">
                    {userType === "assessment-only"
                      ? "Begin your assessment journey and discover your potential through AI-powered insights"
                      : "Select your role below to access the right tools and features for your needs"}
                  </p>
                </div>
              </div>

              <div className="upskill-button-group">
                <button
                  className="upskill-action-button upskill-button-primary"
                  onClick={handleNavigateToMayaMaya}
                  disabled={isLoading}
                >
                  <span className="upskill-button-text">
                    {isLoading ? "Redirecting..." : "Start Assessment"}
                  </span>
                  <ArrowRight size={18} className="upskill-button-icon" />
                </button>
                {userType === "dual-access" && (
                  <button
                    className="upskill-action-button upskill-button-secondary"
                    onClick={handleNavigateToConsole}
                    disabled={isLoading}
                  >
                    <span className="upskill-button-text">
                      {isLoading ? "Loading..." : "Access Business Console"}
                    </span>
                    <ArrowRight size={18} className="upskill-button-icon" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="portal-right-column">
          <div className="portal-info-card portal-info-card-assessment">
            <div className="portal-card-header">
              <div className="logo-container">
                {/* style={{ background: "black", borderRadius: "8px" }} */}
                <img src={Logo} alt="NOAHy Logo" width={78} height={58} />
              </div>
              <div className="portal-card-title-group">
                <h3 className="portal-card-title">Noah Assessment</h3>
                <p className="portal-card-subtitle">For Assessment Takers</p>
              </div>
            </div>

            <div className="portal-card-content">
              <p className="portal-card-description">
                Discover your potential through AI-powered assessments that
                adapt to your responses. Gain personalized insights into your
                soft skills, track your growth, and uncover career opportunities
                tailored to your strengths and aspirations.
              </p>

              <div className="portal-card-features-section">
                <h4 className="portal-features-title">What You'll Get:</h4>
                <ul className="portal-card-features">
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Adaptive journey with 27 interactive questions that evolve
                      based on your answers
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Quick 10–15 minute completion time for efficient and
                      engaging participation
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Career profile assessment and resume upload for deeper,
                      data-backed insights
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      AI-generated report with personalized skill analysis,
                      growth areas, and career recommendations
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="portal-info-card portal-info-card-business">
            <div className="portal-card-header">
              <div className="logo-container">
                <img src={Logo} alt="NOAHy Logo" width={78} height={58} />
              </div>
              <div className="portal-card-title-group">
                <h3 className="portal-card-title">
                  Business Management Interface
                </h3>
                <p className="portal-card-subtitle">For Business Users</p>
              </div>
            </div>

            <div className="portal-card-content">
              <p className="portal-card-description">
                Empower your organization with intelligent talent intelligence.
                Invite participants, monitor assessments in real-time, analyze
                workforce capabilities, and make data-driven decisions for
                strategic growth.
              </p>

              <div className="portal-card-features-section">
                <h4 className="portal-features-title">Key Features:</h4>
                <ul className="portal-card-features">
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Invite & Manage Participants
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Real-time Progress Monitoring
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Advanced Talent Analytics
                    </span>
                  </li>
                  <li className="portal-feature-item">
                    <span className="portal-feature-icon">
                      <CheckCircle size={12} />
                    </span>
                    <span className="portal-feature-text">
                      Strategic Workforce Planning
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
